import type { SketchFeaturePrefixEditInput, SketchFeaturePrefixEditState } from '../../../feature/rollback';
import type {
	SketchFeaturePrefixEditRuntimeDependencies,
} from '../../../../types/viewer/SketchFeaturePrefixEditRuntime';

export type {
	SketchFeaturePrefixEditRuntimeDependencies,
} from '../../../../types/viewer/SketchFeaturePrefixEditRuntime';

/**
 * 严格 Feature 前缀编辑的文档级状态机。
 *
 * 该模块统一管理 single-flight、进入原子性、退出恢复和状态代次；底层 Worker、
 * Mesh、草图会话及 FeatureManager 投影继续复用 Viewer 的既有实现。
 */
export class SketchFeaturePrefixEditRuntime {
	private currentState: SketchFeaturePrefixEditState | null = null;
	private startOperation: {
		readonly sketchGroupId: string;
		readonly task: Promise<boolean>;
	} | null = null;
	private disposed = false;
	private lifecycleGeneration = 0;

	/** @param dependencies Viewer 提供的稳定运行时能力。 */
	public constructor(
		private readonly dependencies: SketchFeaturePrefixEditRuntimeDependencies,
	) {}

	/** 当前活动前缀状态。 */
	public get state(): SketchFeaturePrefixEditState | null {
		return this.currentState;
	}

	/** 是否正在建立前缀。 */
	public get isStarting(): boolean {
		return this.startOperation !== null;
	}

	/** 建立严格 Feature 前缀，并保证同草图 single-flight。 */
	public async start(input: SketchFeaturePrefixEditInput): Promise<boolean> {
		if (this.disposed || this.dependencies.isFeatureEditBusy()) {
			return false;
		}
		if (this.startOperation) {
			return this.startOperation.sketchGroupId === input.sketchGroupId
				? this.startOperation.task
				: false;
		}
		if (this.currentState) {
			return this.currentState.runtimeSketchId === input.sketchGroupId;
		}

		const lifecycleGeneration = this.lifecycleGeneration;
		const task = this.startOnce(input, lifecycleGeneration);
		this.startOperation = { sketchGroupId: input.sketchGroupId, task };
		try {
			const started = await task;
			return this.isLifecycleCurrent(lifecycleGeneration) ? started : false;
		} finally {
			if (this.startOperation?.task === task) {
				this.startOperation = null;
			}
		}
	}

	/** 在草图结束边界恢复完整 Feature 时间线。 */
	public async restoreOnSessionEnd(input: {
		readonly sketchId: string;
		readonly expectedDefinitionRevision: number | null;
	}): Promise<boolean> {
		const state = this.currentState;
		if (!state || state.runtimeSketchId !== input.sketchId) {
			return false;
		}
		if (input.expectedDefinitionRevision === null) {
			throw new Error(`无法恢复严格 Feature 前缀：草图最终定义不存在 ${input.sketchId}`);
		}

		const lifecycleGeneration = this.lifecycleGeneration;
		state.status = 'closing';
		try {
			const result = await this.dependencies.rebuildAfterPrefixEdit({
				sketchId: state.runtimeSketchId,
				fullCommands: state.plan.fullCommands,
				cutoffFeatureIndex: state.plan.cutoffFeatureIndex,
				expectedDefinitionRevision: input.expectedDefinitionRevision,
			});
			if (!this.isLifecycleCurrent(lifecycleGeneration)) {
				return false;
			}
			if (!result.success || (result.status !== 'success' && result.status !== 'stopped')) {
				throw new Error(result.error ?? `严格 Feature 前缀完整恢复未完成：${result.status}`);
			}
		} catch (error) {
			if (!this.isLifecycleCurrent(lifecycleGeneration)) {
				return false;
			}
			const recoveryErrors: unknown[] = [];
			state.status = 'active';
			try {
				if (!this.dependencies.resumeSketch(input.sketchId)) {
					throw new Error(`无法重新进入草图编辑：${input.sketchId}`);
				}
			} catch (recoveryError) {
				recoveryErrors.push(recoveryError);
			}
			try {
				if (this.dependencies.applyDisplayProjection()) {
					this.dependencies.requestRender();
				}
			} catch (recoveryError) {
				recoveryErrors.push(recoveryError);
			}
			if (recoveryErrors.length > 0) {
				console.error('严格 Feature 前缀退出失败后的编辑会话恢复不完整。', {
					sketchGroupId: input.sketchId,
					replayError: error,
					recoveryErrors,
				});
			}
			throw error;
		}

		this.currentState = null;
		if (this.dependencies.applyDisplayProjection()) {
			this.dependencies.requestRender();
		}
		return true;
	}

	/** 清理不会跨文档保留的临时状态。 */
	public dispose(): void {
		// 先撤销旧异步任务的提交权，再清理可观察状态，避免旧 Promise 回写新文档。
		this.lifecycleGeneration += 1;
		this.disposed = true;
		this.currentState = null;
		this.startOperation = null;
	}

	/** 执行一次身份解析和原子前缀回放。 */
	private async startOnce(
		input: SketchFeaturePrefixEditInput,
		lifecycleGeneration: number,
	): Promise<boolean> {
		const identity = this.dependencies.resolveSketchIdentity(input.sketchGroupId);
		if (
			!identity
			|| typeof identity.persistentId !== 'string'
			|| identity.persistentId.length === 0
		) {
			console.warn('无法进入草图 Feature 前缀编辑：目标草图不存在。', {
				sketchGroupId: input.sketchGroupId,
			});
			return false;
		}

		const beforeSummary = await this.dependencies.getGeometryStateSummary();
		if (!this.isLifecycleCurrent(lifecycleGeneration)) {
			return false;
		}
		this.dependencies.syncRollbackHistory(beforeSummary);
		this.dependencies.syncRollbackTimeline();
		const resolution = this.dependencies.resolvePrefix({
			runtimeSketchId: identity.runtimeId,
			persistentSketchId: identity.persistentId,
		});
		if (resolution.success === false) {
			console.warn('无法解析草图严格 Feature 前缀。', {
				sketchGroupId: input.sketchGroupId,
				reason: resolution.reason,
				message: resolution.message,
			});
			return false;
		}

		const state: SketchFeaturePrefixEditState = {
			runtimeSketchId: identity.runtimeId,
			persistentSketchId: identity.persistentId,
			plan: resolution.plan,
			status: 'starting',
		};
		this.currentState = state;
		const prefixCommands = state.plan.fullCommands.slice(0, state.plan.cutoffFeatureIndex);
		let prefixReplayCompleted = false;
		try {
			await this.dependencies.replayPrefix(prefixCommands, beforeSummary);
			if (!this.isLifecycleCurrent(lifecycleGeneration)) {
				return false;
			}
			prefixReplayCompleted = true;
			if (this.dependencies.applyDisplayProjection()) {
				this.dependencies.requestRender();
			}
			state.status = 'active';
			return true;
		} catch (error) {
			if (!this.isLifecycleCurrent(lifecycleGeneration)) {
				return false;
			}
			const recoveryErrors: unknown[] = [];
			if (prefixReplayCompleted) {
				try {
					await this.dependencies.restoreWorker(beforeSummary);
					if (!this.isLifecycleCurrent(lifecycleGeneration)) {
						return false;
					}
				} catch (recoveryError) {
					if (!this.isLifecycleCurrent(lifecycleGeneration)) {
						return false;
					}
					recoveryErrors.push(recoveryError);
				}
			}
			this.currentState = null;
			try {
				if (this.dependencies.applyDisplayProjection()) {
					this.dependencies.requestRender();
				}
			} catch (recoveryError) {
				recoveryErrors.push(recoveryError);
			}
			console.error('进入草图严格 Feature 前缀编辑失败。', {
				sketchGroupId: input.sketchGroupId,
				error,
				recoveryErrors,
			});
			return false;
		}
	}

	/** 判断异步任务是否仍属于当前未销毁生命周期。 */
	private isLifecycleCurrent(lifecycleGeneration: number): boolean {
		return !this.disposed && this.lifecycleGeneration === lifecycleGeneration;
	}
}
